import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Get token from localStorage
function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

// Get cached user from localStorage
function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Save session
function saveSession(data) {
  if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(USER_KEY, JSON.stringify(data));
}

// Clear session
function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// Global axios interceptor - ALWAYS attach token
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

// Response interceptor - catch 401, try refresh, then clear
let isRefreshing = false;
let refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.config?.url?.includes('/api/') && !error.config?.url?.includes('/api/auth/login') && !error.config?._retry) {
      // Try refreshing token once
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = axios.get(`${API}/api/auth/me`).then(({ data }) => {
          saveSession(data);
          isRefreshing = false;
          return data;
        }).catch(() => {
          clearSession();
          isRefreshing = false;
          window.location.href = window.location.pathname.includes('/student') ? '/student' : '/admin';
          return null;
        });
      }
      const result = await refreshPromise;
      if (result) {
        error.config._retry = true;
        error.config.headers.Authorization = `Bearer ${getToken()}`;
        return axios(error.config);
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  // Initialize from localStorage immediately (no loading flicker)
  const [user, setUser] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(() => !getCachedUser());
  const didCheck = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      saveSession(data);
      setUser(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
        setUser(false);
      } else {
        // Network error - keep cached user
        const cached = getCachedUser();
        if (cached) setUser(cached);
        else setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check once, skip StrictMode double-fire
    if (didCheck.current) return;
    didCheck.current = true;
    checkAuth();
  }, [checkAuth]);

  // Auto-refresh token every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const token = getToken();
      if (token) {
        axios.get(`${API}/api/auth/me`).then(({ data }) => {
          saveSession(data);
        }).catch(() => {});
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    if (data.otp_required) return data; // OTP required, don't save session yet
    saveSession(data);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch {}
    clearSession();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be inside AuthProvider');
  return context;
}

// Utility: get stored token for API calls (doesn't depend on React state)
export { getToken, getCachedUser, saveSession, clearSession };
